import React, { useMemo } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { isMobileShellLayout } from '../../utils/mobileShellLayout';
import { isTabletFormFactor } from '../../utils/mobileView';
import { mobileAppGlassCard } from '../layout/mobile/mobileAppTheme';

/**
 * Creative Material UI tab bar: icon above label, pill-style active state.
 * Desktop: horizontal scroll row. Mobile shell / tablet: grid with touch-friendly targets.
 * variant="material" — elevated glass card with floating active chip (Hall-style).
 */
const NotchTabs = ({
  value,
  onChange,
  tabs,
  accentColor = '#4ce1b6',
  mobileRows = 2,
  variant = 'default',
  sx = {},
}) => {
  const theme = useTheme();
  const isMobile = isMobileShellLayout();
  const isTablet = !isMobile && isTabletFormFactor();
  const useGridLayout = isMobile || isTablet;
  const isDark = isMobile || theme.palette.mode === 'dark';
  const isMaterial = variant === 'material' && !useGridLayout;

  const activeFg = isDark ? 'rgba(255,255,255,0.98)' : theme.palette.text.primary;
  const inactiveFg = isDark ? 'rgba(148, 163, 184, 0.88)' : 'rgba(0,0,0,0.55)';
  const activeBg = isMobile
    ? `linear-gradient(145deg, ${accentColor}40 0%, ${accentColor}14 100%)`
    : isDark
      ? `linear-gradient(135deg, ${accentColor}22 0%, ${accentColor}08 100%)`
      : `linear-gradient(135deg, ${accentColor}18 0%, ${accentColor}08 100%)`;
  const hoverBg = isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)';
  const borderColor = isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.08)';
  const surfaceElevation = isDark
    ? '0 2px 8px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.05)'
    : '0 2px 8px rgba(0,0,0,0.06), inset 0 1px 0 rgba(255,255,255,0.8)';

  const materialActiveBg = isDark
    ? `linear-gradient(160deg, ${alpha(accentColor, 0.32)} 0%, ${alpha(accentColor, 0.1)} 55%, ${alpha('#fff', 0.04)} 100%)`
    : `linear-gradient(160deg, ${alpha(accentColor, 0.22)} 0%, ${alpha(accentColor, 0.08)} 45%, #ffffff 100%)`;

  const gridRows = isMobile ? mobileRows : (isTablet ? 3 : mobileRows);
  const mobileColumns = useMemo(
    () => Math.ceil(tabs.length / gridRows),
    [tabs.length, gridRows],
  );

  const getTabLabel = (tab) => {
    if ((isMobile || isTablet) && tab.shortLabel) return tab.shortLabel;
    return tab.label;
  };

  const desktopShellSx = isMaterial
    ? {
        display: 'flex',
        alignItems: 'stretch',
        gap: 0.75,
        p: { xs: 0.75, sm: 1 },
        borderRadius: '22px',
        position: 'relative',
        overflow: 'hidden',
        border: `1px solid ${isDark ? alpha('#fff', 0.12) : alpha('#0f172a', 0.06)}`,
        background: isDark
          ? `linear-gradient(145deg, ${alpha('#1e293b', 0.96)} 0%, ${alpha('#0f172a', 0.92)} 55%, ${alpha(accentColor, 0.08)} 100%)`
          : `linear-gradient(145deg, #ffffff 0%, #f8fafc 48%, ${alpha(accentColor, 0.1)} 100%)`,
        boxShadow: isDark
          ? `
            0 1px 0 ${alpha('#fff', 0.06)} inset,
            0 10px 28px ${alpha('#000', 0.35)},
            0 2px 8px ${alpha(accentColor, 0.12)}
          `
          : `
            0 1px 0 ${alpha('#fff', 0.95)} inset,
            0 12px 32px ${alpha('#0f172a', 0.08)},
            0 4px 12px ${alpha(accentColor, 0.1)},
            0 0 0 1px ${alpha('#fff', 0.4)} inset
          `,
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin',
        '&::before': {
          content: '""',
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          background: isDark
            ? `radial-gradient(120% 80% at 0% 0%, ${alpha(accentColor, 0.18)} 0%, transparent 55%),
               radial-gradient(90% 70% at 100% 100%, ${alpha(accentColor, 0.1)} 0%, transparent 50%)`
            : `radial-gradient(120% 80% at 0% 0%, ${alpha(accentColor, 0.16)} 0%, transparent 55%),
               radial-gradient(90% 70% at 100% 100%, ${alpha(accentColor, 0.08)} 0%, transparent 50%)`,
        },
        '&::-webkit-scrollbar': { height: 6 },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: 3,
          backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
        },
      }
    : {
        display: 'flex',
        alignItems: 'stretch',
        gap: 0.5,
        p: { xs: 0.5, sm: 0.75 },
        borderRadius: 2,
        border: `1px solid ${borderColor}`,
        backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
        boxShadow: surfaceElevation,
        overflowX: 'auto',
        overflowY: 'hidden',
        WebkitOverflowScrolling: 'touch',
        scrollbarWidth: 'thin',
        '&::-webkit-scrollbar': { height: 6 },
        '&::-webkit-scrollbar-thumb': {
          borderRadius: 3,
          backgroundColor: isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.2)',
        },
      };

  return (
    <Box
      role="tablist"
      sx={{
        ...(useGridLayout
          ? {
              ...mobileAppGlassCard,
              display: 'grid',
              gridTemplateColumns: `repeat(${mobileColumns}, minmax(0, 1fr))`,
              gap: isTablet ? 0.65 : 0.5,
              p: isTablet ? 0.85 : 0.75,
              overflow: 'visible',
            }
          : desktopShellSx),
        ...sx,
      }}
    >
      {tabs.map((tab, index) => {
        const isActive = value === index;
        const Icon = tab.icon;
        const label = getTabLabel(tab);

        return (
          <Box
            key={tab.id ?? index}
            role="tab"
            aria-selected={isActive}
            tabIndex={isActive ? 0 : -1}
            onClick={() => onChange?.(null, index)}
            sx={{
              ...(useGridLayout
                ? {
                    width: '100%',
                    minWidth: 0,
                  }
                : {
                    flex: { xs: '0 0 auto', sm: 1 },
                    minWidth: { xs: 64, sm: 0 },
                    maxWidth: { sm: 'none' },
                  }),
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: useGridLayout ? (isTablet ? 0.95 : 0.85) : isMaterial ? { xs: 1.35, sm: 1.75 } : { xs: 1.25, sm: 2 },
              px: useGridLayout ? (isTablet ? 0.5 : 0.35) : isMaterial ? { xs: 1.1, sm: 1.5 } : { xs: 1, sm: 1.25 },
              minHeight: useGridLayout ? (isTablet ? 58 : 52) : isMaterial ? { xs: 72, sm: 88 } : { xs: 56, sm: 68 },
              cursor: 'pointer',
              position: 'relative',
              zIndex: 1,
              borderRadius: useGridLayout ? '12px' : isMaterial ? '16px' : 1.5,
              background: isActive
                ? (isMaterial ? materialActiveBg : activeBg)
                : 'transparent',
              border: isActive
                ? `1px solid ${accentColor}${useGridLayout ? '55' : isMaterial ? '50' : '40'}`
                : '1px solid transparent',
              borderBottom: useGridLayout || isMaterial
                ? undefined
                : (isActive ? `3px solid ${accentColor}` : '3px solid transparent'),
              boxShadow: isActive
                ? (useGridLayout
                  ? `0 6px 16px ${accentColor}22, inset 0 1px 0 rgba(255,255,255,0.08)`
                  : isMaterial
                    ? (isDark
                      ? `0 8px 20px ${alpha(accentColor, 0.22)}, 0 1px 0 ${alpha('#fff', 0.08)} inset`
                      : `0 10px 22px ${alpha(accentColor, 0.2)}, 0 2px 6px ${alpha('#0f172a', 0.06)}, 0 1px 0 ${alpha('#fff', 0.9)} inset`)
                    : 'none')
                : 'none',
              WebkitTapHighlightColor: 'transparent',
              transition: 'all 0.28s cubic-bezier(0.4, 0, 0.2, 1)',
              '&:hover': {
                background: isActive
                  ? (isMaterial ? materialActiveBg : activeBg)
                  : hoverBg,
                borderColor: isActive ? `${accentColor}55` : (isMaterial ? alpha(accentColor, 0.18) : 'transparent'),
                transform: isMaterial && !isActive ? 'translateY(-1px)' : undefined,
              },
              '&:active': {
                transform: 'scale(0.97)',
              },
              ...(isMaterial && isActive
                ? {
                    '&::after': {
                      content: '""',
                      position: 'absolute',
                      left: '22%',
                      right: '22%',
                      bottom: 6,
                      height: 3,
                      borderRadius: 999,
                      background: `linear-gradient(90deg, transparent, ${accentColor}, transparent)`,
                      boxShadow: `0 0 10px ${alpha(accentColor, 0.55)}`,
                    },
                  }
                : {}),
            }}
          >
            {Icon && (
              <Box
                sx={{
                  color: isActive ? accentColor : inactiveFg,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: useGridLayout ? (isTablet ? 0.45 : 0.35) : isMaterial ? { xs: 0.65, sm: 0.85 } : { xs: 0.5, sm: 0.75 },
                  transition: 'color 0.2s ease, transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease',
                  transform: isActive ? 'scale(1.06)' : 'scale(1)',
                  ...(isMaterial
                    ? {
                        width: { xs: 36, sm: 42 },
                        height: { xs: 36, sm: 42 },
                        borderRadius: '14px',
                        background: isActive
                          ? (isDark
                            ? `linear-gradient(145deg, ${alpha(accentColor, 0.35)} 0%, ${alpha(accentColor, 0.12)} 100%)`
                            : `linear-gradient(145deg, ${alpha(accentColor, 0.28)} 0%, ${alpha(accentColor, 0.1)} 100%)`)
                          : (isDark ? alpha('#fff', 0.04) : alpha('#0f172a', 0.04)),
                        boxShadow: isActive
                          ? `0 4px 12px ${alpha(accentColor, 0.28)}, inset 0 1px 0 ${alpha('#fff', 0.25)}`
                          : 'none',
                        border: `1px solid ${isActive ? alpha(accentColor, 0.35) : 'transparent'}`,
                      }
                    : {}),
                  '& .MuiSvgIcon-root': {
                    fontSize: useGridLayout
                      ? (isTablet ? 20 : 18)
                      : isMaterial
                        ? { xs: 20, sm: 22 }
                        : { xs: 20, sm: 24 },
                  },
                }}
              >
                <Icon />
              </Box>
            )}
            <Typography
              component="span"
              sx={{
                fontSize: useGridLayout
                  ? (isTablet ? '0.62rem' : '0.58rem')
                  : isMaterial
                    ? { xs: '0.7rem', sm: '0.78rem' }
                    : { xs: '0.6875rem', sm: '0.75rem' },
                fontWeight: isActive ? 800 : 500,
                color: isActive
                  ? (useGridLayout ? '#f8fafc' : activeFg)
                  : inactiveFg,
                textAlign: 'center',
                lineHeight: 1.15,
                letterSpacing: useGridLayout
                  ? '0.03em'
                  : (isActive ? '0.02em' : '0.01em'),
                textTransform: useGridLayout ? 'uppercase' : 'none',
                transition: 'color 0.2s ease, font-weight 0.2s ease',
                whiteSpace: useGridLayout ? 'normal' : 'nowrap',
                wordBreak: useGridLayout ? 'break-word' : 'normal',
                px: useGridLayout ? 0.25 : 0,
              }}
            >
              {label}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default NotchTabs;
